'use client';

import type { ToolExecutionHar } from '@terra/shared-contracts-tracker';
import {
  isLinkedTicketProvider,
  type LinkedTicketProvider,
} from '@terra/shared-contracts-findings';
import type { MouseEvent, ReactNode } from 'react';
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Box, VStack, HStack } from '@terra/ui-styled-system';
import {
  Badge,
  BodyDefault,
  BodyStrong,
  HeadingItem,
  Skeleton,
  CopyIdButton,
  Tabs,
  Scroll,
  Button,
  FindingDrawerOverviewTab,
  FindingDrawerResolutionTab,
  FindingDrawerBusinessImpactTab,
  FindingDrawerAuditTab,
  FindingsExportDialog,
  EditLinkedTicketDialog,
  MarkdownPreview,
  toasterApi,
  type EditableFields,
  type SearchRunResult,
  createSelectListCollection,
  formatCategoryForDisplay,
  formatFindingStatusForDisplay,
  ConfirmationDialog,
  Tooltip,
  EntityIcon,
  CredentialEntityResolver,
  getChangedEditableFieldKeys,
  getFindingEditExplanationError,
  hasEditableFieldChanges,
  SecurityClassificationBadges,
  ArchivedLifecycleBadge,
  isArchivedLifecycle,
  FindingRetestStatusBadge,
  FindingCvssScoresPill,
} from '@terra/ui-design-system';
import { FindingDetailsHeaderActions } from './FindingDetailsHeaderActions';
import { AssigneeSelector } from './AssigneeSelector';
import { FindingPublicationBadge } from './FindingPublicationBadge';
import { FindingCvssTabPanel } from './FindingCvssTabPanel';
import { FindingDetailsEditView } from './FindingDetailsEditView';
import {
  FindingSaveRejectedError,
  findingUpdateErrorMessage,
} from './finding-update-error-message';
import { FindingDetailsTabTriggers } from './FindingDetailsTabTriggers';
import { useFeatureFlagsContext } from '../../app/providers/FeatureFlagsProvider';
import {
  SeverityChangeRequestHeaderPill,
  scrollToSeverityChangeRequestCard,
} from './SeverityChangeRequestHeaderPill';
import { SeverityChangeRequestReviewCard } from './SeverityChangeRequestReviewCard';
import { useUserTenants } from '../../hooks/api/useUserTenants';
import {
  useMainContentTabs,
  type FindingMainContentTab,
} from '../../store/layoutStore';
import { ENTITY_TAB_TYPE } from '../../store/layoutStoreTypes';
import {
  findingDetailSubTabKey,
  useTabViewStateStore,
} from '../../store/tabViewStateStore';
import { Logger } from '../../electron/logger';
import { TestTube01Icon } from '@terra/ui-icons';
import {
  BadgeSeverity,
  FindingSeverityBadge,
  BadgeStatus,
  FINDING_STATUS_BADGE_STATUS_MAP,
  isMappedFindingStatus,
  BADGE_STATUS,
  ChatHarProvider,
  downloadPocScriptAsFile,
  FindingPocSectionActions,
  PocActionsMode,
  usePocScriptActions,
  StructuredPoc,
} from '@terra/ui-design-system';
import { useChatStore } from '../../store/chatStore';
import { useTranslations } from 'next-intl';
import { terraProxyApiClient } from '../../clients/testerClient';
import { useFindingById } from '../../hooks/api/useFindingById';
import { useSyncCvssScoreOnSettle } from '../../hooks/useSyncCvssScoreOnSettle';
import { usePublishFinding } from '../../hooks/api/usePublishFinding';
import { invalidateSignalQueriesAfterFindingStatusChange } from '../../hooks/api/invalidateSignalQueriesAfterFindingStatusChange';
import { useFindingListNavigationStore } from '../../store/findingListNavigationStore';
import { useFindingsBySignalId } from '../../hooks/api/useFindingsBySignalId';
import { useReplaceFindingLinkedTicket } from '../../hooks/api/useReplaceFindingLinkedTicket';
import { useCredentials } from '../../hooks/api/useCredentials';
import { useFindingRetestWorkflow } from '../../hooks/useFindingRetestWorkflow';
import {
  getGeneratePocScriptErrorMessage,
  getFindingTimelineExecutionDisplayText,
  resolveExecutionOpenTitle,
} from '@terra/ui-design-system';
import { findingsClient } from '@terra/shared-rest-clients-findings';
import { useFindingTimeline } from '../../hooks/useFindingTimeline';
import { useFindingAuditLogs } from '../../hooks/useFindingAuditLogs';
import { useFindingPocUpdatedSSE } from '../../hooks/useFindingPocUpdatedSSE';
import { useFindingsPdfExport } from '../../hooks/useFindingsPdfExport';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import {
  useTargetId,
  useAppTargetId,
  useTargetType,
} from '../../store/projectStore';
import { generateExecutionTabTitle } from '../../utils/tabTitleUtils';
import { isCoPilotExecutionAgent } from '../../utils/is-co-pilot-execution-agent';
import { isPublishedFindingVisibleToCustomers } from '../../utils/is-published-finding-visible-to-customers';
import {
  enrichFindingEditSignalLabel,
  resolveFindingEditSignalIdToEnrich,
  resolveFindingEditSignalSeedLabel,
  type FindingEditSignalSearchResult,
} from '../../utils/findingEditSignalLabelEnrichment';
import { useFindingEditStore } from '../../store/findingEditStore';
import { useSyncInlineFindingEditLayout } from '../../hooks/layout/useSyncInlineFindingEditLayout';
import { useFindingCopilotSuggestionReview } from '../../hooks/useFindingCopilotSuggestionReview';
import { useFindingStatusReasonDialog } from '../../hooks/useFindingStatusReasonDialog';
import { useFindingCvssReasonsChange } from '../../hooks/useFindingCvssReasonsChange';
import {
  isPublishedAfterFindingSave,
  requiresUnpublishedFindingSignalConfirm,
  useUnpublishedFindingSignalConfirm,
} from '../../hooks/useUnpublishedFindingSignalConfirm';
import { markObjectUpdatesConsumed } from '../../store/consumedObjectUpdatesStore';
import { FindingLinkedTicketActions } from './FindingLinkedTicketActions';
import {
  UnpublishedConstituentsDialog,
  type UnpublishedConstituentsViolation,
} from '../dialogs/UnpublishedConstituentsDialog';
import {
  getSignalCategoriesForTargetType,
  AssetTypeSchema,
  type PocBlock,
  type FindingStatusType,
  type AssetTypeType,
} from '@terra/shared-schemas-orchestrator';
import type { FindingUpdateBodyDto } from '@terra/shared-contracts-findings';
import type { SearchAssetResult, EnrichedAsset } from '@terra/ui-design-system';
import { createListCollection } from '@ark-ui/react';
import {
  type FindingEditAssetItem,
  type FindingEditRunItem,
  type FindingEditSignalItem,
} from './findingEditForm.types';
import { useHarById } from '../../hooks/api/useHarById';
import { useGlobalContextMenu } from '../../hooks/useGlobalContextMenu';
import { GlobalMenuItemsTypeEnum } from '../../store/globalContextMenuStore';
import { createFindingObjectContextMention } from '../../utils/objectContextMentionUtils';
import {
  buildEditableFieldsFromFinding,
  mergeHydratedFindingFields,
  needsFindingHydration,
  needsServerOwnedOwaspRiskHydration,
} from '../../utils/findingEditableHydration';
import {
  shouldPersistFindingEditDraft,
  shouldShowFindingEditSurface,
} from '../../utils/findingSuggestionFieldUtils';
import { useApplicationById } from '../../hooks/api/useApplicationById';
import {
  getCvssVersionFromVector,
  resolveFindingSelectedCvssVersion,
} from '@terra/shared-cvss';
import { getSignalExternalNetworkAssetTab } from '../../utils/signalSurfaceAssetNavigation';
import { ObjectContextMarkableField } from '../object-context/ObjectContextMarkableField';

type FindingDetailsTabProps = {
  tab: FindingMainContentTab;
};

const FINDING_DETAILS_CONTENT_TABS = [
  'overview',
  'resolution',
  'business-impact',
  'audit',
  'cvss',
] as const;

type FindingDetailsContentTab = (typeof FINDING_DETAILS_CONTENT_TABS)[number];

const isFindingDetailsContentTab = (
  value: string,
): value is FindingDetailsContentTab =>
  (FINDING_DETAILS_CONTENT_TABS as readonly string[]).includes(value);

const FINDING_STATUSES = [
  'Resolved',
  'Unresolved',
  'InProgress',
  'FalsePositive',
  'WontFix',
  'Retest',
] as const;
const RETEST_STATUS: FindingStatusType = 'Retest';
const statusOptions = createSelectListCollection<{
  label: string;
  value: string;
}>({
  items: FINDING_STATUSES.map((status) => ({
    label: formatFindingStatusForDisplay(status),
    value: status,
  })),
});
const NULLABLE_FIELDS: Array<keyof EditableFields> = [
  'shortDescription',
  'description',
  'poc',
  'businessImpact',
  'mitigationPlan',
  'signalId',
  'traceThreadId',
  'findingUpdateComment',
];

type SearchSignalResult = FindingEditSignalSearchResult;
type FindingSearchAssetResult = SearchAssetResult & {
  graphqlOperationName?: string | null;
};

type AssetLabelSource = Pick<EnrichedAsset, 'id' | 'type'> &
  Partial<
    Pick<
      EnrichedAsset,
      'url' | 'path' | 'method' | 'host' | 'graphqlOperationName'
    >
  >;

const toSearchAssetResult = ({
  id,
  type,
  url,
  path,
  method,
  host,
  graphqlOperationName,
}: AssetLabelSource): FindingSearchAssetResult => ({
  id,
  url: url || path || host || id,
  method: method || undefined,
  type,
  graphqlOperationName: graphqlOperationName || undefined,
});

const assetHasDisplayLabel = ({
  url,
  path,
  host,
}: Pick<AssetLabelSource, 'url' | 'path' | 'host'>) =>
  Boolean(url?.trim() || path?.trim() || host?.trim());

const getAssetLabelQuality = ({
  id,
  url,
  method,
  graphqlOperationName,
}: Pick<
  FindingSearchAssetResult,
  'id' | 'url' | 'method' | 'graphqlOperationName'
>): number => {
  const label = url?.trim() ?? '';

  if (!label || label === id) {
    return 0;
  }

  let score = 1;

  if (label.includes('://')) {
    score += 4;
  } else if (label.startsWith('/')) {
    score += 2;
  }

  if (method) {
    score += 1;
  }

  if (graphqlOperationName) {
    score += 1;
  }

  return score;
};

const preferRicherAssetLabel = ({
  current,
  next,
}: {
  current?: FindingSearchAssetResult;
  next: FindingSearchAssetResult;
}): FindingSearchAssetResult => {
  if (!current) {
    return next;
  }

  return getAssetLabelQuality(next) > getAssetLabelQuality(current)
    ? next
    : current;
};

const resolveAssetSearchResult = async (
  asset: EnrichedAsset,
): Promise<FindingSearchAssetResult> => {
  if (assetHasDisplayLabel(asset)) {
    return toSearchAssetResult(asset);
  }

  const { id, type } = asset;

  if (type === 'ENDPOINT') {
    const endpoint = await terraProxyApiClient
      .getEndpointById(id)
      .catch((error: unknown) => {
        console.warn('Failed to hydrate endpoint asset label', error);

        return null;
      });

    if (endpoint) {
      return toSearchAssetResult({
        ...asset,
        url: endpoint.url,
        path: endpoint.path,
        method: endpoint.request?.method ?? null,
        graphqlOperationName: endpoint.graphqlOperationName,
      });
    }
  }

  if (type === 'WEBPAGE') {
    const webpage = await terraProxyApiClient
      .getWebPageById(id)
      .catch((error: unknown) => {
        console.warn('Failed to hydrate webpage asset label', error);

        return null;
      });

    if (webpage) {
      return toSearchAssetResult({
        ...asset,
        url: webpage.url,
      });
    }
  }

  return toSearchAssetResult(asset);
};

const logger = new Logger('FindingDetailsTab');

export function FindingDetailsTab({ tab }: FindingDetailsTabProps) {
  const t = useTranslations('designSystem.findings.editDrawer');
  const tCopilotTabTitles = useTranslations('chat.copilot.tabTitles');
  const tFindingDetail = useTranslations('designSystem.findings.findingDrawer');
  const tFindingsRow = useTranslations('findingsTab.row');
  const tLifecycle = useTranslations('designSystem.lifecycle');
  const findingOverviewT = useTranslations(
    'designSystem.findings.findingDrawer.tabs.overview',
  );
  const targetId = useTargetId();
  const targetType = useTargetType();
  const appTargetId = useAppTargetId();
  const queryClient = useQueryClient();
  const {
    navigateInActiveTab,
    openTab,
    closeTab,
    goBackInActiveTab,
    canGoBackInActiveTab,
  } = useMainContentTabs();
  const { sendHttpMentionsToChat } = useChatStore();
  const { fetchHarById } = useHarById();
  const { openContextMenu } = useGlobalContextMenu();
  const { credentials = [] } = useCredentials({ applicationId: appTargetId });
  const { isTerraUser } = useUserTenants();
  const { isFeatureFlagEnabled: isSeverityChangeRequestEnabled } =
    useFeatureFlags('SEVERITY_CHANGE_REQUEST_ENABLED');
  const { application: applicationForCvss } = useApplicationById({
    id: appTargetId,
  });
  const cvssCalculatorVersion =
    applicationForCvss?.cvssVersion === 'V3_1' ? '3.1' : '4.0';
  const handleFetchHarData = useCallback(
    async ({
      threadId,
      toolCallId,
    }: {
      threadId: string;
      toolCallId: string;
    }): Promise<ToolExecutionHar[]> => {
      try {
        const response = await terraProxyApiClient.getToolHarsByToolCallId({
          threadId,
          toolCallId,
        });

        return response?.hars || [];
      } catch (error) {
        console.error('[ChatInstance] Error fetching HAR data:', error);

        return [];
      }
    },
    [],
  );
  const {
    mutateAsync: replaceFindingLinkedTicket,
    isPending: isReplacingLinkedTicket,
  } = useReplaceFindingLinkedTicket();
  const findingId = tab.data.findingId;
  const initialMode = tab.data.mode === 'edit' ? 'edit' : 'view';
  const { featureFlags } = useFeatureFlagsContext();
  const detailSubTabEntityKey = findingId
    ? findingDetailSubTabKey(findingId)
    : undefined;
  const savedDetailSubTab = useTabViewStateStore((state) =>
    detailSubTabEntityKey
      ? state.detailSubTabs[detailSubTabEntityKey]
      : undefined,
  );
  const setDetailSubTab = useTabViewStateStore(
    (state) => state.setDetailSubTab,
  );
  const activeDetailSubTab = useMemo((): FindingDetailsContentTab => {
    const allowed = new Set<string>([
      'overview',
      'resolution',
      'business-impact',
      'audit',
    ]);

    if (featureFlags.FINDING_CVSS_DETAILS_TAB_ENABLED) {
      allowed.add('cvss');
    }

    if (
      savedDetailSubTab &&
      allowed.has(savedDetailSubTab) &&
      isFindingDetailsContentTab(savedDetailSubTab)
    ) {
      return savedDetailSubTab;
    }

    return 'overview';
  }, [featureFlags.FINDING_CVSS_DETAILS_TAB_ENABLED, savedDetailSubTab]);
  const handleDetailSubTabChange = useCallback(
    (nextTab: FindingDetailsContentTab) => {
      if (!detailSubTabEntityKey) {
        return;
      }

      setDetailSubTab(detailSubTabEntityKey, nextTab);
    },
    [detailSubTabEntityKey, setDetailSubTab],
  );
  const handleScrollToSeverityChangeRequestCard = useCallback(() => {
    if (activeDetailSubTab !== 'overview') {
      handleDetailSubTabChange('overview');

      return;
    }

    scrollToSeverityChangeRequestCard();
  }, [activeDetailSubTab, handleDetailSubTabChange]);
  const setFindingEditState = useFindingEditStore(
    (state) => state.setFindingEditState,
  );
  const getFindingEditState = useFindingEditStore(
    (state) => state.getFindingEditState,
  );
  const clearFindingEditDraftAfterSave = useFindingEditStore(
    (state) => state.clearFindingEditDraftAfterSave,
  );
  const consumeAcceptedObjectUpdateSourceIds = useFindingEditStore(
    (state) => state.consumeAcceptedObjectUpdateSourceIds,
  );
  const sendSelectionToChatLabel = 'Send to Chat';
  const storedEditState = findingId
    ? getFindingEditState(findingId)
    : undefined;
  const hasPendingAiEditsOnMount =
    findingId != null &&
    (useFindingEditStore.getState().getPendingAiEdits(findingId)?.fields
      .length ?? 0) > 0;
  const shouldRestorePersistedStateOnMount =
    initialMode !== 'view' ||
    storedEditState?.mode === 'edit' ||
    Boolean(storedEditState?.hasUnsavedChanges) ||
    hasPendingAiEditsOnMount;
  const persistedState = shouldRestorePersistedStateOnMount
    ? storedEditState
    : undefined;
  const findingIds = useFindingListNavigationStore((state) => state.findingIds);
  const { publishFinding, isPublishing } = usePublishFinding();
  const [mode, setMode] = useState<'view' | 'edit'>(
    hasPendingAiEditsOnMount ? 'edit' : persistedState?.mode || initialMode,
  );

  // Export state
  const [isExportDialogOpen, setExportDialogOpen] = useState(false);
  // Edit state - use persisted state if available
  const [editableFields, setEditableFields] = useState<EditableFields>(
    persistedState?.editableFields || {
      title: '',
      shortDescription: '',
      description: '',
      poc: '',
      pocBlocks: null,
      businessImpact: '',
      mitigationPlan: '',
      status: 'Unresolved',
      severity: 'Low',
      category: null,
      assignedTo: '',
      signalId: null,
      traceThreadId: null,
      assets: [],
      isPublished: null,
      findingUpdateComment: null,
      cvss: null,
      owaspRisk: null,
    },
  );
  const findingCvssVersion = editableFields.cvss?.vector
    ? getCvssVersionFromVector(editableFields.cvss.vector)
    : null;
  const [originalFields, setOriginalFields] = useState<EditableFields | null>(
    persistedState?.originalFields || null,
  );
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(
    persistedState?.hasUnsavedChanges || false,
  );
  const {
    isCopilotProcessing,
    hasPendingSuggestions,
    suggestionReviewBar,
    getMarkableFieldProps,
    handleStopCopilotWorking,
    clearCopilotSuggestionsForFinding,
    clearReviewSession,
    clearReviewFocus,
    discardPendingSelectSuggestionOnManualChange,
  } = useFindingCopilotSuggestionReview({
    findingId,
    editableFields,
    setEditableFields,
    originalFields,
    setMode,
    setHasUnsavedChanges,
    getFindingEditState,
  });
  const isEditSurface = shouldShowFindingEditSurface({
    mode,
    hasPendingSuggestions,
    isCopilotProcessing,
  });

  useSyncInlineFindingEditLayout({
    findingId,
    isEditMode: isEditSurface,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isRetestStatusConfirmationOpen, setIsRetestStatusConfirmationOpen] =
    useState(false);
  const [
    isScheduledRetestConfirmationOpen,
    setIsScheduledRetestConfirmationOpen,
  ] = useState(false);
  const [
    unpublishedConstituentsViolations,
    setUnpublishedConstituentsViolations,
  ] = useState<UnpublishedConstituentsViolation[] | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const categoryOptions = useMemo(() => {
    const categories = [
      ...getSignalCategoriesForTargetType(targetType ?? 'WEB_APPLICATION'),
    ];

    if (
      editableFields.category &&
      !categories.includes(editableFields.category)
    ) {
      categories.push(editableFields.category);
    }

    return createSelectListCollection<{ label: string; value: string }>({
      items: [
        { label: 'Select Category...', value: '' },
        ...categories.map((category) => ({
          label: formatCategoryForDisplay(category),
          value: category,
        })),
      ],
    });
  }, [editableFields.category, targetType]);
  // Search states - restore from persisted state if available
  const [signalSearchResults, setSignalSearchResults] = useState<
    SearchSignalResult[]
  >(persistedState?.signalSearchResults || []);
  const [signalDisplayById, setSignalDisplayById] = useState<
    Record<string, SearchSignalResult>
  >({});
  const [runSearchResults, setRunSearchResults] = useState<SearchRunResult[]>(
    persistedState?.runSearchResults || [],
  );
  const [assetSearchResults, setAssetSearchResults] = useState<
    FindingSearchAssetResult[]
  >(persistedState?.assetSearchResults || []);
  const [assetDisplayById, setAssetDisplayById] = useState<
    Record<string, FindingSearchAssetResult>
  >({});
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const [isLinkedTicketDialogOpen, setIsLinkedTicketDialogOpen] =
    useState(false);
  // Search loading states
  const [isSearchingSignals, setIsSearchingSignals] = useState(false);
  const [isSearchingRuns, setIsSearchingRuns] = useState(false);
  const [isSearchingAssets, setIsSearchingAssets] = useState(false);
  // Search timeout refs
  const signalSearchTimeout = useRef<NodeJS.Timeout | null>(null);
  const runSearchTimeout = useRef<NodeJS.Timeout | null>(null);
  const assetSearchTimeout = useRef<NodeJS.Timeout | null>(null);
  const selectedTraceThreadIdRef = useRef<string | null | undefined>(undefined);
  const selectedSignalIdRef = useRef<string | null>(editableFields.signalId);
  const activeFindingIdRef = useRef<string | null>(findingId ?? null);
  const initializedEditableFindingIdRef = useRef<string | null>(
    persistedState ? (findingId ?? null) : null,
  );

  selectedTraceThreadIdRef.current = editableFields.traceThreadId;
  selectedSignalIdRef.current = editableFields.signalId;
  activeFindingIdRef.current = findingId ?? null;
  useEffect(() => {
    return () => {
      if (activeFindingIdRef.current === findingId) {
        activeFindingIdRef.current = null;
      }
    };
  }, [findingId]);
  const {
    finding,
    isLoading,
    error,
    refetch: refetchFinding,
    invalidate: invalidateFindingById,
  } = useFindingById({
    targetId,
    findingId,
  });

  useEffect(() => {
    if (!error) {
      return;
    }

    logger.warn('Failed to load finding detail', {
      findingId,
      error: error instanceof Error ? error.message : String(error),
    });
  }, [error, findingId]);

  useSyncCvssScoreOnSettle({
    finding,
    baselineFindingId: initializedEditableFindingIdRef.current,
    originalFields,
    setEditableFields,
    setOriginalFields,
  });
  const constituentFindingIds = useMemo(
    () => finding?.constituentFindingIds ?? [],
    [finding?.constituentFindingIds],
  );
  const { findings: linkedFindings } = useFindingsBySignalId({
    targetId,
    targetType,
    signalId: null,
    findingIds: constituentFindingIds,
    enabled: constituentFindingIds.length > 0,
  });
  const navigateToFinding = useCallback(
    (targetFindingId: string) => {
      navigateInActiveTab({
        id: `${ENTITY_TAB_TYPE.FINDING}-${targetFindingId}`,
        title: tCopilotTabTitles('findingDetails'),
        type: ENTITY_TAB_TYPE.FINDING,
        data: { findingId: targetFindingId, mode: 'view' },
      });
    },
    [navigateInActiveTab, tCopilotTabTitles],
  );
  const {
    exportFindingsPdf,
    isExporting,
    exportStatus,
    progress: exportProgress,
    error: exportError,
    reset: resetExport,
  } = useFindingsPdfExport({ applicationId: appTargetId });
  const generatePocScriptApi = useCallback(
    async (params: { applicationId: string; findingId: string }) => {
      const response = await findingsClient.generatePocScript({
        params,
        body: {},
      });

      if (response.status !== 202) {
        const body =
          response.body && typeof response.body === 'object'
            ? response.body
            : undefined;
        const fallback =
          response.status === 400 || response.status === 404
            ? 'Request failed'
            : 'Failed to start POC script generation';

        throw new Error(getGeneratePocScriptErrorMessage(body, fallback));
      }
    },
    [],
  );
  const updateFindingPocApi = useCallback(
    async (
      findingIdParam: string,
      data: {
        pocScript: null;
        pocScriptBlockedByGuardrails: null;
        pocScriptGuardrailsFeedback: null;
      },
    ) => {
      await terraProxyApiClient.updateFinding(findingIdParam, data);
    },
    [],
  );
  const refetchFindingWrapped = useCallback(async () => {
    const result = await refetchFinding();

    return { data: result.data };
  }, [refetchFinding]);
  const invalidateQueries = useCallback(async () => {
    await invalidateFindingById();
  }, [invalidateFindingById]);
  const pocScriptActions = usePocScriptActions({
    applicationId: appTargetId ?? undefined,
    findingId: findingId ?? undefined,
    finding: finding ?? null,
    generatePocScriptApi,
    updateFindingPocApi,
    refetchFinding: refetchFindingWrapped,
    invalidateQueries,
    t,
    waitForSseCompletion: true,
  });
  const pocScriptTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const setPocGenerationComplete = pocScriptActions.setPocGenerationComplete;
  const onPocUpdatedFromSse = useCallback(() => {
    if (pocScriptTimeoutRef.current) {
      clearTimeout(pocScriptTimeoutRef.current);
      pocScriptTimeoutRef.current = null;
    }

    refetchFinding()
      .then((result) => {
        const data = result.data;

        if (data?.pocScript && data?.id) {
          setPocGenerationComplete();
          downloadPocScriptAsFile({
            pocScript: data.pocScript,
            findingId: data.id,
          });
        }
      })
      .catch(() => undefined);
  }, [setPocGenerationComplete, refetchFinding]);

  useFindingPocUpdatedSSE({
    findingId: findingId ?? null,
    applicationId: appTargetId ?? null,
    enabled: pocScriptActions.isGeneratingPocScript,
    onPocUpdated: onPocUpdatedFromSse,
  });
  const POC_SCRIPT_TIMEOUT_MS = 120000;

  useEffect(() => {
    if (!pocScriptActions.isGeneratingPocScript || !findingId) return;
    pocScriptTimeoutRef.current = setTimeout(() => {
      pocScriptTimeoutRef.current = null;
      setPocGenerationComplete();
      toasterApi.warning({
        title: t('generatePocScriptButton'),
        description: t('pocScriptTimeoutDescription'),
      });
    }, POC_SCRIPT_TIMEOUT_MS);

    return () => {
      if (pocScriptTimeoutRef.current) {
        clearTimeout(pocScriptTimeoutRef.current);
        pocScriptTimeoutRef.current = null;
      }
    };
  }, [
    pocScriptActions.isGeneratingPocScript,
    findingId,
    setPocGenerationComplete,
    t,
  ]);
  // Reset state when findingId changes to prevent showing stale data from previous finding
  useEffect(() => {
    if (!findingId) return;

    const storedState = getFindingEditState(findingId);
    const pendingCount =
      useFindingEditStore.getState().getPendingAiEdits(findingId)?.fields
        .length ?? 0;
    const shouldRestorePersistedState =
      initialMode !== 'view' ||
      storedState?.mode === 'edit' ||
      Boolean(storedState?.hasUnsavedChanges) ||
      pendingCount > 0;
    const newPersistedState = shouldRestorePersistedState
      ? storedState
      : undefined;

    if (newPersistedState) {
      setMode(pendingCount > 0 ? 'edit' : newPersistedState.mode);
      setEditableFields(newPersistedState.editableFields);
      setOriginalFields(newPersistedState.originalFields);
      setHasUnsavedChanges(newPersistedState.hasUnsavedChanges);
      setIsSaving(false);
      setSignalSearchResults(newPersistedState.signalSearchResults || []);
      setRunSearchResults(newPersistedState.runSearchResults || []);
      setAssetSearchResults(newPersistedState.assetSearchResults || []);
      initializedEditableFindingIdRef.current = findingId;
    } else {
      setMode(pendingCount > 0 ? 'edit' : initialMode);
      setEditableFields({
        title: '',
        shortDescription: '',
        description: '',
        poc: '',
        pocBlocks: null,
        businessImpact: '',
        mitigationPlan: '',
        status: 'Unresolved',
        severity: 'Low',
        category: null,
        assignedTo: '',
        signalId: null,
        traceThreadId: null,
        assets: [],
        isPublished: null,
        findingUpdateComment: null,
        cvss: null,
        owaspRisk: null,
      });
      setOriginalFields(null);
      setHasUnsavedChanges(false);
      setIsSaving(false);
      setSignalSearchResults([]);
      setRunSearchResults([]);
      setAssetSearchResults([]);
      setSaveError(null);
      initializedEditableFindingIdRef.current = null;
    }
  }, [findingId, getFindingEditState, initialMode]);
  // Initialize editable fields when finding loads
  useEffect(() => {
    if (finding) {
      const alreadyInitializedForFinding =
        initializedEditableFindingIdRef.current === finding.id;
      const awaitingInitialTitleHydration =
        originalFields === null &&
        needsFindingHydration({
          editableFields,
          findingTitle: finding.title,
        });
      const shouldSkipHydration =
        alreadyInitializedForFinding && !awaitingInitialTitleHydration;

      if (shouldSkipHydration) {
        if (
          needsServerOwnedOwaspRiskHydration({
            editableFields,
            originalFields,
            findingOwaspRisk: finding.owaspRisk,
          })
        ) {
          setEditableFields((previous) => ({
            ...previous,
            owaspRisk: finding.owaspRisk ?? null,
          }));
          setOriginalFields((previous) =>
            previous
              ? { ...previous, owaspRisk: finding.owaspRisk ?? null }
              : previous,
          );
        }

        return;
      }

      const findingFields = buildEditableFieldsFromFinding({ finding });
      const shouldMergeWithCurrentFields =
        alreadyInitializedForFinding && awaitingInitialTitleHydration;
      const fields = shouldMergeWithCurrentFields
        ? mergeHydratedFindingFields({
            current: editableFields,
            findingFields,
          })
        : findingFields;

      setEditableFields(fields);
      if (!shouldMergeWithCurrentFields) {
        setOriginalFields(fields);
        setHasUnsavedChanges(false);
      }

      setSaveError(null);
      initializedEditableFindingIdRef.current = finding.id;
      if (findingId && finding.id === findingId) {
        const existingEditState = getFindingEditState(findingId);
        const hasPendingAiEdits =
          (useFindingEditStore.getState().getPendingAiEdits(findingId)?.fields
            .length ?? 0) > 0;
        const shouldPreserveSharedEditState =
          existingEditState?.hasUnsavedChanges ||
          existingEditState?.mode === 'edit' ||
          hasPendingAiEdits;

        if (!shouldPreserveSharedEditState) {
          setFindingEditState(findingId, {
            editableFields: fields,
            mode: alreadyInitializedForFinding ? mode : initialMode,
            originalFields: shouldMergeWithCurrentFields
              ? (originalFields ?? fields)
              : fields,
          });
        }
      }

      // Immediately populate asset search results so combobox can display properly
      if (finding.assets && finding.assets.length > 0) {
        setAssetSearchResults(finding.assets.map(toSearchAssetResult));
      } else {
        setAssetSearchResults([]);
        setAssetDisplayById({});
      }

      if (finding.signal) {
        const signalCreatedAt = finding.signal.createdAt;
        const seededSignal: SearchSignalResult = {
          id: finding.signal.id,
          label: resolveFindingEditSignalSeedLabel({
            signalId: finding.signal.id,
            signalDescription: finding.signal.description,
            signalSummaryTitle: finding.signalSummary?.title,
          }),
          category: finding.signal.category || undefined,
          description: finding.signal.description || undefined,
          status: finding.signal.status || undefined,
          createdAt:
            typeof signalCreatedAt === 'string'
              ? signalCreatedAt
              : signalCreatedAt?.toISOString?.() || undefined,
        };

        setSignalSearchResults([seededSignal]);
        setSignalDisplayById({ [seededSignal.id]: seededSignal });
      } else if (finding.signalId) {
        const seededSignal: SearchSignalResult = {
          id: finding.signalId,
          label: resolveFindingEditSignalSeedLabel({
            signalId: finding.signalId,
            signalSummaryTitle: finding.signalSummary?.title,
          }),
          category: finding.signalSummary?.category || undefined,
          status: finding.signalSummary?.status || undefined,
        };

        setSignalSearchResults([seededSignal]);
        setSignalDisplayById({ [seededSignal.id]: seededSignal });
      } else {
        setSignalSearchResults([]);
        setSignalDisplayById({});
      }
    }
  }, [
    finding,
    findingId,
    mode,
    initialMode,
    originalFields,
    editableFields,
    getFindingEditState,
    setFindingEditState,
  ]);

  useEffect(() => {
    const signalId = resolveFindingEditSignalIdToEnrich({
      signal: finding?.signal,
      signalId: finding?.signalId,
      signalSummary: finding?.signalSummary,
    });

    if (!signalId) {
      return;
    }

    let cancelled = false;

    void enrichFindingEditSignalLabel({
      signalId,
      findingId,
      activeFindingIdRef,
      selectedSignalIdRef,
      setSignalDisplayById,
      setSignalSearchResults,
      isCancelled: () => cancelled,
    });

    return () => {
      cancelled = true;
    };
  }, [finding?.signal, finding?.signalId, finding?.signalSummary, findingId]);
  // Track unsaved changes
  useEffect(() => {
    if (originalFields && isEditSurface) {
      setHasUnsavedChanges(
        hasEditableFieldChanges({ editableFields, originalFields }),
      );
    } else {
      setHasUnsavedChanges(false);
    }
  }, [editableFields, originalFields, isEditSurface]);
  useEffect(() => {
    if (tab.data.mode === 'edit' && mode === 'view') {
      setMode('edit');
    }
  }, [tab.data.mode, mode]);
  const persistDraftForFindingIdRef = useRef(findingId);

  const syncFindingEditStateToStore = useCallback(() => {
    const findingIdChanged = persistDraftForFindingIdRef.current !== findingId;

    persistDraftForFindingIdRef.current = findingId;

    if (
      findingIdChanged ||
      !findingId ||
      !shouldPersistFindingEditDraft({
        mode,
        hasPendingSuggestions,
      }) ||
      !originalFields ||
      initializedEditableFindingIdRef.current !== findingId
    ) {
      return;
    }

    setFindingEditState(findingId, {
      mode: 'edit',
      editableFields,
      originalFields,
      hasUnsavedChanges,
      signalSearchResults,
      runSearchResults,
      assetSearchResults,
    });
  }, [
    findingId,
    hasPendingSuggestions,
    mode,
    editableFields,
    originalFields,
    hasUnsavedChanges,
    signalSearchResults,
    runSearchResults,
    assetSearchResults,
    setFindingEditState,
  ]);

  useEffect(() => {
    syncFindingEditStateToStore();
  }, [syncFindingEditStateToStore]);

  const handleEnterEditMode = useCallback(() => {
    setMode('edit');

    if (!findingId || !originalFields) {
      return;
    }

    setFindingEditState(findingId, {
      mode: 'edit',
      editableFields,
      originalFields,
      hasUnsavedChanges,
      signalSearchResults,
      runSearchResults,
      assetSearchResults,
    });
  }, [
    findingId,
    originalFields,
    editableFields,
    hasUnsavedChanges,
    signalSearchResults,
    runSearchResults,
    assetSearchResults,
    setFindingEditState,
  ]);
  const {
    timeline: findingTimeline,
    isLoading: isLoadingTimeline,
    invalidate: invalidateTimeline,
  } = useFindingTimeline({
    id: findingId || null,
    enabled: !!findingId,
  });

  useEffect(() => {
    if (!findingTimeline?.execution) return;
    const execution = findingTimeline.execution;
    const runId = execution.threadId;

    if (!runId) return;
    const newRun: SearchRunResult = {
      id: runId,
      name: getFindingTimelineExecutionDisplayText({
        execution,
        executionLabel: t('entityTabTitle.trace'),
      }),
      subtitle: undefined,
      createdAt: execution.createdAt
        ? new Date(execution.createdAt).toISOString()
        : undefined,
      agentName: execution.agentName ?? undefined,
    };

    setRunSearchResults((prev) => {
      const existingRun = prev.find((r) => r.id === newRun.id);

      if (
        existingRun &&
        existingRun.name === newRun.name &&
        existingRun.subtitle === newRun.subtitle
      ) {
        return prev;
      }

      return [newRun];
    });
  }, [findingTimeline, t]);
  const {
    auditLogs,
    isLoading: isLoadingAuditLogs,
    invalidate: invalidateAuditLogs,
  } = useFindingAuditLogs({
    id: findingId || null,
    enabled: !!findingId,
  });
  const isDetailReady = !!finding && !isLoading;
  const {
    isRetesting,
    retestCompletedResult,
    activeRetestThreadId,
    activeRetestTraceId,
    activeRetestExecutionId,
    requestRetest,
    retestAbort,
  } = useFindingRetestWorkflow({
    findingId: findingId ?? '',
    targetType: finding?.targetType,
    findingStatus: finding?.status,
    retestThreadIds: finding?.retestThreadIds,
    scheduledRetestAt: finding?.scheduledRetestAt,
    isDetailReady,
    auditLogs,
    findingTimeline,
    refetchFinding,
    refetchFindingTimeline: invalidateTimeline,
    refetchAuditLogs: invalidateAuditLogs,
  });
  const wasRetested =
    (finding?.hasRetests ?? false) || retestCompletedResult === 'success';
  const wasRetestedBadgeLabel = wasRetested
    ? findingOverviewT('wasRetestedYes')
    : findingOverviewT('wasRetestedNo');
  const showRetestStatusBadge =
    isRetesting ||
    retestAbort.isShowingAbortedChip ||
    retestCompletedResult === 'fail';
  // Get date formatter
  const dateFormatter = useDateFormatter();
  const handleViewTrace = useCallback(
    (
      {
        threadId,
        title: traceTitle,
        agentName,
        executionId,
      }: {
        traceId: string;
        threadId: string;
        executionId?: string | null;
        title?: string;
        agentName?: string | null;
      },
      event?: MouseEvent<HTMLElement>,
    ) => {
      const isContextMenu = event?.type === 'contextmenu';
      const title = generateExecutionTabTitle({
        executionTitle: resolveExecutionOpenTitle({
          title: traceTitle ?? findingTimeline?.execution?.title,
          agentName:
            agentName ??
            (findingTimeline?.execution?.threadId === threadId
              ? findingTimeline.execution.agentName
              : undefined),
        }),
        fallback: t('entityTabTitle.trace'),
      });
      const matchedRun = runSearchResults.find((run) => run.id === threadId);
      const resolvedAgentName =
        agentName ??
        matchedRun?.agentName ??
        (findingTimeline?.execution?.threadId === threadId
          ? findingTimeline.execution.agentName
          : undefined);
      const isCoPilotExecution =
        matchedRun?.agentName === 'co-pilot' ||
        isCoPilotExecutionAgent(resolvedAgentName);
      const resolvedExecutionId =
        executionId ?? findingTimeline?.execution?.executionId ?? null;
      const tabData = isCoPilotExecution
        ? {
            id: `co-pilot-execution-${threadId}`,
            title,
            type: 'co-pilot-execution' as const,
            data: {
              threadId,
              title: traceTitle ?? findingTimeline?.execution?.title,
              targetId: finding?.targetId,
            },
          }
        : resolvedExecutionId
          ? {
              id: `agent-execution-${resolvedExecutionId}`,
              title,
              type: 'agent-execution' as const,
              data: { executionId: resolvedExecutionId },
            }
          : {
              id: `${ENTITY_TAB_TYPE.EXECUTIONS}-${threadId}`,
              title,
              type: ENTITY_TAB_TYPE.EXECUTIONS,
              data: { threadId },
            };

      isContextMenu
        ? openContextMenu({
            event,
            items: [
              { type: GlobalMenuItemsTypeEnum.OPEN_IN_NEW_TAB, tab: tabData },
            ],
          })
        : isCoPilotExecution
          ? openTab(tabData)
          : navigateInActiveTab(tabData);
    },
    [
      finding,
      findingTimeline,
      navigateInActiveTab,
      openContextMenu,
      openTab,
      runSearchResults,
      t,
    ],
  );
  const handleViewSignal = useCallback(
    (signalId: string, event?: MouseEvent<HTMLElement>) => {
      const isContextMenu = event?.type === 'contextmenu';
      const tabData = {
        id: `${ENTITY_TAB_TYPE.SIGNAL}-${signalId}`,
        title: findingTimeline?.signal?.title || t('entityTabTitle.signal'),
        type: ENTITY_TAB_TYPE.SIGNAL,
        data: { signalId: signalId },
      };

      isContextMenu
        ? openContextMenu({
            event,
            items: [
              { type: GlobalMenuItemsTypeEnum.OPEN_IN_NEW_TAB, tab: tabData },
            ],
          })
        : navigateInActiveTab(tabData);
    },
    [findingTimeline, navigateInActiveTab, openContextMenu, t],
  );
  const handleViewAsset = useCallback(
    (
      asset: {
        id: string;
        type: AssetTypeType;
        discoveredAssetType?: string | null;
      },
      event?: MouseEvent<HTMLElement>,
    ) => {
      const isContextMenu = event?.type === 'contextmenu';
      const surfaceAssetTab = getSignalExternalNetworkAssetTab({
        assetId: asset.id,
        assetType: asset.discoveredAssetType,
        targetType: finding?.targetType,
      });

      if (asset.type === AssetTypeSchema.enum.DISCOVERED_ASSET) {
        if (!surfaceAssetTab) {
          return;
        }

        if (isContextMenu) {
          openContextMenu({
            event,
            items: [
              {
                type: GlobalMenuItemsTypeEnum.OPEN_IN_NEW_TAB,
                tab: surfaceAssetTab,
              },
            ],
          });

          return;
        }

        navigateInActiveTab(surfaceAssetTab);

        return;
      }

      const isEndpoint = asset.type === AssetTypeSchema.enum.ENDPOINT;
      const tabData = isEndpoint
        ? {
            id: `${ENTITY_TAB_TYPE.ENDPOINT}-${asset.id}`,
            title: t('entityTabTitle.endpoint'),
            type: ENTITY_TAB_TYPE.ENDPOINT,
            data: { endpointId: asset.id },
          }
        : {
            id: `${ENTITY_TAB_TYPE.WEBPAGE}-${asset.id}`,
            title: t('entityTabTitle.webpage'),
            type: ENTITY_TAB_TYPE.WEBPAGE,
            data: { id: asset.id },
          };

      isContextMenu
        ? openContextMenu({
            event,
            items: [
              {
                type: GlobalMenuItemsTypeEnum.OPEN_IN_NEW_TAB,
                tab: tabData,
              },
            ],
          })
        : navigateInActiveTab(tabData);
    },
    [finding?.targetType, navigateInActiveTab, openContextMenu, t],
  );
  const handleViewCodeChange = (url: string) => {
    window.open(url, '_blank');
  };

  // Search handlers
  const handleSignalSearch = useCallback(
    async (query: string) => {
      if (signalSearchTimeout.current) {
        clearTimeout(signalSearchTimeout.current);
      }

      if (!query.trim()) {
        return;
      }

      if (!appTargetId) {
        return;
      }

      setIsSearchingSignals(true);
      signalSearchTimeout.current = setTimeout(async () => {
        try {
          const result = await terraProxyApiClient.getSignalsByApplication(
            appTargetId,
            {
              pagination: { limit: 20 },
              filters: { description: query },
            },
          );
          const signals = result?.signals ?? [];

          setSignalSearchResults(
            signals.map((signal) => ({
              id: signal.id,
              label: signal.description?.slice(0, 80) || signal.id.slice(0, 8),
              category: signal.category as string | undefined,
              description: signal.description || undefined,
              status: signal.status as string | undefined,
            })),
          );
        } catch (error: unknown) {
          logger.warn('Failed to search signals for finding edit', {
            applicationId: appTargetId,
            error: error instanceof Error ? error.message : String(error),
          });
        } finally {
          setIsSearchingSignals(false);
        }
      }, 300);
    },
    [appTargetId],
  );
  const handleRunSearch = useCallback(
    async (query: string) => {
      if (runSearchTimeout.current) {
        clearTimeout(runSearchTimeout.current);
      }

      if (!appTargetId || !query.trim()) {
        setRunSearchResults([]);

        return;
      }

      setIsSearchingRuns(true);
      runSearchTimeout.current = setTimeout(async () => {
        try {
          const results: SearchRunResult[] = [];
          const isUUID =
            /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
              query,
            );

          try {
            const traceResponse =
              await terraProxyApiClient.getTracesByApplication({
                applicationId: appTargetId,
                threadId: isUUID ? query : undefined,
                name: isUUID ? undefined : query || undefined,
                agentName: 'neo',
              });

            if (traceResponse.status === 200) {
              traceResponse.body.traces.forEach((trace) => {
                const shortThreadId = trace.threadId.slice(0, 8);
                const agentName = trace.name ?? t('entityTabTitle.trace');

                results.push({
                  id: trace.threadId,
                  name: `${agentName} • ${shortThreadId}`,
                  subtitle: undefined,
                  createdAt: trace.createdAt
                    ? new Date(trace.createdAt).toISOString()
                    : undefined,
                  agentName: trace.name ?? undefined,
                });
              });
            }
          } catch (error) {
            console.error('Failed to search traces:', error);
          }

          const normalizedQuery = query.trim();

          if (normalizedQuery.length > 0) {
            try {
              const conversationsResponse =
                await terraProxyApiClient.getConversations({
                  targetId: appTargetId,
                  search: normalizedQuery,
                });
              const coPilotLabel = t('entityTabTitle.coPilot');

              conversationsResponse.conversations.forEach((conversation) => {
                if (!conversation.threadId) {
                  return;
                }

                const title = conversation.title ?? t('entityTabTitle.trace');
                const shortThreadId = conversation.threadId.slice(0, 8);

                results.push({
                  id: conversation.threadId,
                  name: `${coPilotLabel} • ${title} • ${shortThreadId}`,
                  subtitle: undefined,
                  createdAt: conversation.updatedAt,
                  agentName: 'co-pilot',
                });
              });
            } catch (error) {
              console.error('Failed to search co-pilot traces:', error);
            }
          }

          results.sort((a, b) => {
            if (!a.createdAt || !b.createdAt) return 0;

            return (
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            );
          });
          setRunSearchResults((prev) => {
            const selectedId = selectedTraceThreadIdRef.current;

            if (!selectedId) return results;
            if (results.some((r) => r.id === selectedId)) return results;
            const selected = prev.find((r) => r.id === selectedId);

            return selected ? [selected, ...results] : results;
          });
        } catch {
          setRunSearchResults((prev) => {
            const selectedId = selectedTraceThreadIdRef.current;

            if (!selectedId) return [];
            const selected = prev.find((r) => r.id === selectedId);

            return selected ? [selected] : [];
          });
        } finally {
          setIsSearchingRuns(false);
        }
      }, 300);
    },
    [appTargetId, t],
  );
  const getBaselineAssetSearchResults =
    useCallback((): FindingSearchAssetResult[] => {
      const byId = new Map<string, FindingSearchAssetResult>();

      const resolveBaselineAsset = ({
        asset,
        fromFinding,
      }: {
        asset: AssetLabelSource;
        fromFinding?: AssetLabelSource;
      }): FindingSearchAssetResult =>
        preferRicherAssetLabel({
          current: assetDisplayById[asset.id],
          next: toSearchAssetResult(fromFinding ?? asset),
        });

      for (const asset of finding?.assets ?? []) {
        byId.set(
          asset.id,
          resolveBaselineAsset({
            asset,
            fromFinding: asset,
          }),
        );
      }

      for (const asset of editableFields.assets) {
        if (byId.has(asset.id)) {
          continue;
        }

        const fromFinding = finding?.assets?.find(
          (findingAsset) => findingAsset.id === asset.id,
        );

        byId.set(
          asset.id,
          resolveBaselineAsset({
            asset,
            fromFinding,
          }),
        );
      }

      return [...byId.values()];
    }, [assetDisplayById, editableFields.assets, finding?.assets]);

  const linkedAssetsEnrichmentKey =
    finding?.assets
      ?.map((asset) =>
        [
          asset.id,
          asset.url ?? '',
          asset.path ?? '',
          asset.host ?? '',
          asset.method ?? '',
          asset.graphqlOperationName ?? '',
        ].join(':'),
      )
      .join('|') ?? '';
  const findingAssetsRef = useRef(finding?.assets);

  findingAssetsRef.current = finding?.assets;

  useEffect(() => {
    const assets = findingAssetsRef.current ?? [];

    if (assets.length === 0) {
      return;
    }

    let cancelled = false;

    const hydrateAssetLabels = async () => {
      const resolved = await Promise.all(
        assets.map((asset) => resolveAssetSearchResult(asset)),
      );

      if (cancelled) {
        return;
      }

      setAssetDisplayById((previous) => {
        const next = { ...previous };

        for (const asset of resolved) {
          next[asset.id] = preferRicherAssetLabel({
            current: previous[asset.id],
            next: asset,
          });
        }

        return next;
      });
      setAssetSearchResults((previous) => {
        if (previous.length === 0) {
          return resolved;
        }

        const byId = new Map(previous.map((asset) => [asset.id, asset]));

        for (const asset of resolved) {
          byId.set(
            asset.id,
            preferRicherAssetLabel({
              current: byId.get(asset.id),
              next: asset,
            }),
          );
        }

        return [...byId.values()];
      });
    };

    void hydrateAssetLabels();

    return () => {
      cancelled = true;
    };
  }, [finding?.id, linkedAssetsEnrichmentKey]);

  const handleAssetSearch = useCallback(
    async (query: string) => {
      if (assetSearchTimeout.current) {
        clearTimeout(assetSearchTimeout.current);
      }

      if (!query.trim()) {
        setAssetSearchResults(getBaselineAssetSearchResults());

        return;
      }

      if (!appTargetId) {
        return;
      }

      setIsSearchingAssets(true);
      assetSearchTimeout.current = setTimeout(async () => {
        try {
          const isId =
            /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
              query,
            ) || query.length === 24;

          if (isId) {
            try {
              const endpoint = await terraProxyApiClient.getEndpointById(query);

              if (endpoint) {
                setAssetSearchResults([
                  {
                    id: endpoint.id,
                    url: endpoint.url || endpoint.path || endpoint.id,
                    method: endpoint.request?.method,
                    type: 'ENDPOINT' as const,
                  },
                  ...getBaselineAssetSearchResults().filter(
                    (asset) => asset.id !== endpoint.id,
                  ),
                ]);

                return;
              }
            } catch {
              try {
                const webpage = await terraProxyApiClient.getWebPageById(query);

                if (webpage) {
                  setAssetSearchResults([
                    {
                      id: webpage.id,
                      url: webpage.url || webpage.id,
                      type: 'WEBPAGE' as const,
                    },
                    ...getBaselineAssetSearchResults().filter(
                      (asset) => asset.id !== webpage.id,
                    ),
                  ]);

                  return;
                }
              } catch {
                // Continue to search
              }
            }
          }

          const [endpointsResult, webpagesResult] = await Promise.all([
            terraProxyApiClient.getEndpointsPreview(appTargetId, {
              url: query,
              limit: 10,
            }),
            terraProxyApiClient.getWebPagesByApplication(appTargetId, {
              url: query,
              limit: 10,
            }),
          ]);
          const assets: FindingSearchAssetResult[] = [];

          if (endpointsResult?.endpoints) {
            assets.push(
              ...endpointsResult.endpoints.map((endpoint) => ({
                id: endpoint.id,
                url: endpoint.url,
                method: endpoint.request.method,
                type: 'ENDPOINT' as const,
                graphqlOperationName: endpoint.graphqlOperationName,
              })),
            );
          }

          if (webpagesResult?.webPages) {
            assets.push(
              ...webpagesResult.webPages.map((webpage) => ({
                id: webpage.id,
                url: webpage.url,
                type: 'WEBPAGE' as const,
              })),
            );
          }

          const searchIds = new Set(assets.map((asset) => asset.id));
          const merged = [
            ...assets,
            ...getBaselineAssetSearchResults().filter(
              (asset) => !searchIds.has(asset.id),
            ),
          ];

          setAssetSearchResults(merged);
          setAssetDisplayById((previous) => ({
            ...previous,
            ...Object.fromEntries(
              assets
                .filter((asset) => asset.url !== asset.id)
                .map((asset) => [asset.id, asset]),
            ),
          }));
        } catch {
          setAssetSearchResults(getBaselineAssetSearchResults());
        } finally {
          setIsSearchingAssets(false);
        }
      }, 300);
    },
    [appTargetId, getBaselineAssetSearchResults],
  );

  useEffect(() => {
    const selectedSignalId = editableFields.signalId;

    if (!selectedSignalId) {
      setSignalDisplayById({});

      return;
    }

    const fromSearch = signalSearchResults.find(
      (signal) => signal.id === selectedSignalId,
    );

    if (!fromSearch) {
      setSignalDisplayById((previous) => {
        const current = previous[selectedSignalId];

        return current ? { [selectedSignalId]: current } : {};
      });

      return;
    }

    setSignalDisplayById((previous) => {
      const current = previous[selectedSignalId];

      if (current?.description && !fromSearch.description) {
        return { [selectedSignalId]: current };
      }

      return { [selectedSignalId]: fromSearch };
    });
  }, [editableFields.signalId, signalSearchResults]);

  // Collections for comboboxes
  const signalsCollection = useMemo(() => {
    const byId = new Map<string, SearchSignalResult>();

    for (const signal of signalSearchResults) {
      byId.set(signal.id, signal);
    }

    const selectedSignalId = editableFields.signalId;
    const selectedDisplay = selectedSignalId
      ? signalDisplayById[selectedSignalId]
      : undefined;

    if (selectedDisplay) {
      const existing = byId.get(selectedDisplay.id);

      byId.set(
        selectedDisplay.id,
        existing?.description && !selectedDisplay.description
          ? existing
          : { ...existing, ...selectedDisplay },
      );
    }

    return createListCollection<FindingEditSignalItem>({
      items: [...byId.values()].map((signal) => ({
        value: signal.id,
        label: signal.label,
        category: signal.category,
        status: signal.status,
        createdAt: signal.createdAt,
      })),
    });
  }, [editableFields.signalId, signalDisplayById, signalSearchResults]);
  const runsCollection = useMemo(
    () =>
      createListCollection<FindingEditRunItem>({
        items: runSearchResults.map((run) => ({
          value: run.id,
          label: run.name,
          subtitle: run.subtitle,
          createdAt: run.createdAt,
        })),
      }),
    [runSearchResults],
  );
  const assetsCollection = useMemo(() => {
    const byId = new Map<string, FindingSearchAssetResult>();

    for (const asset of getBaselineAssetSearchResults()) {
      byId.set(asset.id, asset);
    }

    for (const asset of assetSearchResults) {
      byId.set(asset.id, asset);
    }

    return createListCollection<FindingEditAssetItem>({
      items: [...byId.values()].map((asset) => ({
        value: asset.id,
        label: asset.graphqlOperationName
          ? `${asset.url}?${asset.graphqlOperationName}`
          : asset.url,
        method: asset.method,
        type: asset.type,
        graphqlOperationName: asset.graphqlOperationName,
      })),
    });
  }, [assetSearchResults, getBaselineAssetSearchResults]);
  // Field change handler
  const handleFieldChange = useCallback(
    <T extends keyof EditableFields>(field: T, value: EditableFields[T]) => {
      discardPendingSelectSuggestionOnManualChange(field, value);
      setEditableFields((prev) => ({ ...prev, [field]: value }));
      setSaveError(null);
    },
    [discardPendingSelectSuggestionOnManualChange],
  );
  const handlePocBlocksChange = useCallback(
    (blocks: PocBlock[]) => {
      handleFieldChange('pocBlocks', blocks.length > 0 ? blocks : null);
    },
    [handleFieldChange],
  );
  const getPocImageIds = useCallback(
    (blocks: PocBlock[] | null | undefined) => {
      return new Set(
        (blocks ?? [])
          .filter((block) => block.type === 'image')
          .map((block) => block.imageId),
      );
    },
    [],
  );
  const deleteDiscardedPocImages = useCallback(
    async ({
      baseBlocks,
      currentBlocks,
      currentFindingId,
    }: {
      baseBlocks: PocBlock[] | null | undefined;
      currentBlocks: PocBlock[] | null | undefined;
      currentFindingId: string | null;
    }) => {
      if (!currentFindingId) {
        return;
      }

      const currentImageIds = getPocImageIds(currentBlocks);

      if (currentImageIds.size === 0) {
        return;
      }

      const baseImageIds = getPocImageIds(baseBlocks);
      const imageIdsToDelete = [...currentImageIds].filter(
        (imageId) => !baseImageIds.has(imageId),
      );

      if (imageIdsToDelete.length === 0) {
        return;
      }

      const results = await Promise.allSettled(
        imageIdsToDelete.map((imageId) =>
          findingsClient.deleteFindingPocImage({
            params: { id: currentFindingId, imageId },
          }),
        ),
      );

      if (results.some((result) => result.status === 'rejected')) {
        console.error('Failed to delete discarded finding POC images');
      }
    },
    [getPocImageIds],
  );
  const handleRunChange = useCallback((traceThreadId: string | null) => {
    setEditableFields((prev) => ({
      ...prev,
      traceThreadId,
    }));
    setSaveError(null);
  }, []);
  const currentRunValue = useMemo(
    () => editableFields.traceThreadId ?? null,
    [editableFields.traceThreadId],
  );
  // Status change with confirmation for certain statuses
  const handleStatusFieldChange = useCallback(
    (newStatus: FindingStatusType) => {
      handleFieldChange('status', newStatus);
    },
    [handleFieldChange],
  );
  // CVSS change handler - matches CvssCalculator's onScoreChange signature
  const handleCvssScoreChange = useCallback(
    (score: string, vector: string) => {
      setEditableFields((prev) => {
        const editedVersion =
          (vector ? getCvssVersionFromVector(vector) : null) ??
          resolveFindingSelectedCvssVersion({
            cvssVersion: finding?.cvssVersion,
            cvss: prev.cvss,
          });
        const versionField = editedVersion === '3.1' ? 'cvss31' : 'cvss40';
        const previousRecord =
          prev[versionField] ??
          (prev.cvss?.version === editedVersion ? prev.cvss : null);
        const newCvss =
          score && vector
            ? {
                score,
                vector,
                version: getCvssVersionFromVector(vector) ?? editedVersion,
                environmentalScore: previousRecord?.environmentalScore ?? null,
                environmentalVector:
                  previousRecord?.environmentalVector ?? null,
                metricReasons: previousRecord?.metricReasons ?? [],
                explanation: previousRecord?.explanation ?? null,
              }
            : null;

        return {
          ...prev,
          cvss: newCvss,
          [versionField]: newCvss,
        };
      });
      setSaveError(null);
    },
    [finding?.cvssVersion],
  );
  const handleCvssReasonsChange =
    useFindingCvssReasonsChange(setEditableFields);
  const handleOwaspRiskScoreChange = useCallback(
    (score: string, vector: string, level: string) => {
      handleFieldChange(
        'owaspRisk',
        score && vector && level ? { score, vector, level } : null,
      );
    },
    [handleFieldChange],
  );
  // Asset handlers

  const handleAssetsMultiChange = useCallback(
    (selectedIds: string[]) => {
      if (selectedIds.length === 0) {
        return;
      }

      setEditableFields((prev) => {
        const existingById = new Map(
          prev.assets.map((asset) => [asset.id, asset]),
        );
        const assets = selectedIds.map((id) => {
          const existing = existingById.get(id);

          if (existing) {
            return existing;
          }

          const fromSearch = assetSearchResults.find(
            (asset) => asset.id === id,
          );

          return {
            id,
            type: (fromSearch?.type ?? 'ENDPOINT') as AssetTypeType,
          };
        });

        return { ...prev, assets };
      });
      setSaveError(null);
    },
    [assetSearchResults],
  );

  const saveFindingChangesRef = useRef<
    (options?: {
      skipRetestConfirmation?: boolean;
      statusNote?: string;
    }) => Promise<boolean>
  >(async () => false);
  const { confirmDisproveSignal, dialog: signalFalsePositiveDialog } =
    useUnpublishedFindingSignalConfirm();
  const { beginIfRequired, dialog: statusReasonDialog } =
    useFindingStatusReasonDialog({
      isSubmitting: isSaving,
      onSubmit: async ({ note }) =>
        saveFindingChangesRef.current({
          skipRetestConfirmation: true,
          statusNote: note,
        }),
    });

  const saveFindingChanges = useCallback(
    async (options?: {
      skipRetestConfirmation?: boolean;
      statusNote?: string;
    }) => {
      if (!findingId || !originalFields) return false;
      if (finding?.id !== findingId) return false;
      const savingFindingId = findingId;
      const invalidAssets = editableFields.assets.filter(
        (asset) => !asset.id || asset.id.trim() === '',
      );

      if (invalidAssets.length > 0) {
        setSaveError(
          'Please select a valid asset for all linked assets, or remove empty entries',
        );

        return false;
      }

      const updates = getChangedEditableFieldKeys({
        editableFields,
        originalFields,
      }).reduce<Partial<EditableFields>>((acc, key) => {
        const rawValue = editableFields[key];
        const value =
          NULLABLE_FIELDS.includes(key) && rawValue === '' ? null : rawValue;

        if (value === undefined) {
          return acc;
        }

        return { ...acc, [key]: value };
      }, {});

      if (updates.assets?.length === 0) {
        delete updates.assets;
      }

      const explanationError = getFindingEditExplanationError({
        editableFields,
        originalFields,
        cvssVersion: finding.cvssVersion,
      });

      if (explanationError) {
        setSaveError(explanationError);

        return false;
      }

      if (Object.keys(updates).length === 0) {
        const savedFields = { ...editableFields };

        setOriginalFields(savedFields);
        setHasUnsavedChanges(false);
        clearFindingEditDraftAfterSave(findingId);

        return true;
      }

      const isChangingStatusToRetest =
        updates.status === RETEST_STATUS &&
        originalFields.status !== RETEST_STATUS;

      if (
        isChangingStatusToRetest &&
        options?.skipRetestConfirmation !== true
      ) {
        setIsRetestStatusConfirmationOpen(true);

        return false;
      }

      if (
        updates.status &&
        originalFields.status &&
        !options?.statusNote &&
        beginIfRequired({
          currentStatus: originalFields.status,
          nextStatus: updates.status,
        })
      ) {
        return false;
      }

      const showUnpublishedConstituentsViolation = (
        violations: UnpublishedConstituentsViolation[],
      ) => {
        const revertedFields: EditableFields = {
          ...editableFields,
          isPublished: originalFields.isPublished,
        };

        setEditableFields(revertedFields);
        if (
          !hasEditableFieldChanges({
            editableFields: revertedFields,
            originalFields,
          })
        ) {
          clearFindingEditDraftAfterSave(savingFindingId);
        }

        setUnpublishedConstituentsViolations(violations);
      };

      const isPublishingChain =
        updates.isPublished === true &&
        originalFields.isPublished !== true &&
        constituentFindingIds.length > 0;

      if (isPublishingChain) {
        const unpublishedLinked = linkedFindings.filter(
          (f) => !isPublishedFindingVisibleToCustomers(f),
        );

        if (unpublishedLinked.length > 0) {
          showUnpublishedConstituentsViolation([
            {
              chainFindingId: findingId,
              chainTitle: finding?.title ?? null,
              unpublishedConstituents: unpublishedLinked.map((f) => ({
                id: f.id,
                title: f.title ?? null,
              })),
            },
          ]);

          return false;
        }
      }

      const needsUnpublishedSignalConfirm = Boolean(
        updates.status &&
          requiresUnpublishedFindingSignalConfirm({
            isPublished: isPublishedAfterFindingSave({
              originalIsPublished: originalFields.isPublished,
              isPublishedUpdate: updates.isPublished,
            }),
            nextStatus: updates.status,
            signalId: finding?.signalId,
          }),
      );
      const disproveRelatedSignal = needsUnpublishedSignalConfirm
        ? await confirmDisproveSignal()
        : false;

      if (needsUnpublishedSignalConfirm && !disproveRelatedSignal) {
        return false;
      }

      setIsSaving(true);
      setSaveError(null);

      try {
        const { cvss40, cvss31, ...updateFields } = updates;
        const updateBody: FindingUpdateBodyDto = {
          ...updateFields,
          ...(cvss40 ? { cvss40 } : {}),
          ...(cvss31 ? { cvss31 } : {}),
          ...(options?.statusNote ? { note: options.statusNote } : {}),
          ...(disproveRelatedSignal ? { disproveRelatedSignal: true } : {}),
        };
        const response = await terraProxyApiClient.updateFinding(
          savingFindingId,
          updateBody,
        );

        if (response.status === 409) {
          const serverViolations = (
            response.body as {
              violations: UnpublishedConstituentsViolation[];
            }
          ).violations;

          if (serverViolations.length === 0) {
            throw new FindingSaveRejectedError(
              findingUpdateErrorMessage(response.body),
            );
          }

          showUnpublishedConstituentsViolation(serverViolations);

          return false;
        }

        if (response.status !== 200) {
          throw new FindingSaveRejectedError(
            findingUpdateErrorMessage(response.body),
          );
        }

        const sourceUpdateIds =
          consumeAcceptedObjectUpdateSourceIds(savingFindingId);

        if (sourceUpdateIds.length > 0) {
          markObjectUpdatesConsumed(sourceUpdateIds);
        }

        clearFindingEditDraftAfterSave(savingFindingId);
        if (activeFindingIdRef.current === savingFindingId) {
          clearReviewSession();
        }

        if (updates.status) {
          invalidateSignalQueriesAfterFindingStatusChange({
            queryClient,
            signalId: response.body.signalId,
          });
        }

        const refetchResult = await refetchFinding();

        await invalidateFindingById();
        await invalidateAuditLogs();
        await invalidateTimeline();
        if (activeFindingIdRef.current !== savingFindingId) {
          return true;
        }

        const savedFinding = refetchResult.data;

        if (savedFinding) {
          const fields = buildEditableFieldsFromFinding({
            finding: savedFinding,
          });

          setEditableFields(fields);
          setOriginalFields(fields);
          initializedEditableFindingIdRef.current = savedFinding.id;
        }

        setMode('view');
        setHasUnsavedChanges(false);
        setSaveError(null);

        return true;
      } catch (err) {
        logger.warn('Failed to save finding', {
          findingId: savingFindingId,
          error: err instanceof Error ? err.message : String(err),
        });

        const saveErrorMessage =
          err instanceof FindingSaveRejectedError
            ? err.message
            : t('saveError');

        if (activeFindingIdRef.current === savingFindingId) {
          setSaveError(saveErrorMessage);
        }

        toasterApi.error({
          title: t('saveErrorTitle'),
          description: saveErrorMessage,
        });

        return false;
      } finally {
        if (activeFindingIdRef.current === savingFindingId) {
          setIsSaving(false);
        }
      }
    },
    [
      findingId,
      finding?.id,
      finding?.signalId,
      finding?.title,
      finding?.cvssVersion,
      originalFields,
      editableFields,
      clearFindingEditDraftAfterSave,
      consumeAcceptedObjectUpdateSourceIds,
      clearReviewSession,
      refetchFinding,
      invalidateFindingById,
      invalidateAuditLogs,
      invalidateTimeline,
      queryClient,
      constituentFindingIds,
      linkedFindings,
      beginIfRequired,
      confirmDisproveSignal,
      t,
    ],
  );

  saveFindingChangesRef.current = saveFindingChanges;
  const handleSave = useCallback(() => {
    void saveFindingChanges();
  }, [saveFindingChanges]);
  const handleConfirmRetestStatusSave = useCallback(async () => {
    setIsRetestStatusConfirmationOpen(false);
    await saveFindingChanges({ skipRetestConfirmation: true });
  }, [saveFindingChanges]);
  const handleRequestRetest = useCallback(async () => {
    if (!findingId || finding?.id !== findingId || !finding.signalId) {
      return;
    }

    if (finding.scheduledRetestAt) {
      setIsScheduledRetestConfirmationOpen(true);

      return;
    }

    setSaveError(null);
    await requestRetest();
  }, [
    findingId,
    finding?.id,
    finding?.signalId,
    finding?.scheduledRetestAt,
    requestRetest,
  ]);
  const handleConfirmScheduledRetest = useCallback(async () => {
    setIsScheduledRetestConfirmationOpen(false);
    setSaveError(null);
    await requestRetest();
  }, [requestRetest]);
  const handleViewActiveRetestTrace = useCallback(() => {
    if (!activeRetestThreadId) {
      return;
    }

    handleViewTrace({
      traceId: activeRetestTraceId ?? '',
      threadId: activeRetestThreadId,
      executionId: activeRetestExecutionId,
    });
  }, [
    activeRetestExecutionId,
    activeRetestThreadId,
    activeRetestTraceId,
    handleViewTrace,
  ]);
  const handleReset = useCallback(() => {
    if (originalFields) {
      void deleteDiscardedPocImages({
        baseBlocks: originalFields.pocBlocks,
        currentBlocks: editableFields.pocBlocks,
        currentFindingId: findingId,
      });
      setEditableFields(originalFields);
      setHasUnsavedChanges(false);
      setSaveError(null);
    }

    clearCopilotSuggestionsForFinding();
    clearReviewFocus();
  }, [
    clearCopilotSuggestionsForFinding,
    clearReviewFocus,
    deleteDiscardedPocImages,
    editableFields.pocBlocks,
    findingId,
    originalFields,
  ]);
  const handleCancel = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowCloseConfirmation(true);
    } else {
      clearCopilotSuggestionsForFinding();
      const isDedicatedEditTab = tab.data?.mode === 'edit';

      if (isDedicatedEditTab) {
        if (canGoBackInActiveTab) {
          goBackInActiveTab();
        } else {
          closeTab(tab.id);
        }
      } else {
        setMode('view');
      }

      if (findingId) {
        clearFindingEditDraftAfterSave(findingId);
      }
    }
  }, [
    hasUnsavedChanges,
    clearCopilotSuggestionsForFinding,
    tab.data?.mode,
    tab.id,
    closeTab,
    canGoBackInActiveTab,
    goBackInActiveTab,
    findingId,
    clearFindingEditDraftAfterSave,
  ]);
  const handleConfirmClose = useCallback(() => {
    setShowCloseConfirmation(false);
    if (originalFields) {
      void deleteDiscardedPocImages({
        baseBlocks: originalFields.pocBlocks,
        currentBlocks: editableFields.pocBlocks,
        currentFindingId: findingId,
      });
      setEditableFields(originalFields);
      setHasUnsavedChanges(false);
    }

    setSaveError(null);
    clearCopilotSuggestionsForFinding();
    const isDedicatedEditTab = tab.data?.mode === 'edit';

    if (isDedicatedEditTab) {
      if (canGoBackInActiveTab) {
        goBackInActiveTab();
      } else {
        closeTab(tab.id);
      }
    } else {
      setMode('view');
    }

    if (findingId) {
      clearFindingEditDraftAfterSave(findingId);
    }
  }, [
    deleteDiscardedPocImages,
    editableFields.pocBlocks,
    findingId,
    originalFields,
    tab.data?.mode,
    tab.id,
    closeTab,
    canGoBackInActiveTab,
    goBackInActiveTab,
    clearFindingEditDraftAfterSave,
    clearCopilotSuggestionsForFinding,
  ]);
  const handleSendToChat = useCallback(() => {
    if (!finding) return;
    const mention = createFindingObjectContextMention(finding);

    sendHttpMentionsToChat({
      mentions: [mention],
      linkedObject: {
        kind: 'Finding',
        id: finding.id,
        label: finding.title,
      },
    });
  }, [finding, sendHttpMentionsToChat]);
  const openFindingIdRef = useRef(findingId);

  openFindingIdRef.current = findingId;
  const handleFindingPublicationChange = useCallback(
    (publishedFindingId: string, isPublished: boolean) => {
      if (publishedFindingId !== openFindingIdRef.current) {
        return;
      }

      let nextFields: EditableFields | undefined;
      let nextOriginal: EditableFields | null | undefined;

      setEditableFields((previousFields) => {
        nextFields = {
          ...previousFields,
          isPublished,
        };

        return nextFields;
      });
      setOriginalFields((previousOriginal) => {
        nextOriginal = previousOriginal
          ? {
              ...previousOriginal,
              isPublished,
            }
          : previousOriginal;

        return nextOriginal;
      });
      setSaveError(null);

      if (nextFields) {
        setFindingEditState(publishedFindingId, {
          editableFields: nextFields,
          mode,
          originalFields: nextOriginal,
        });
      }
    },
    [mode, setFindingEditState],
  );
  const handlePublishFinding = useCallback(async () => {
    if (!findingId) {
      return;
    }

    const requestFindingId = findingId;
    const didPublish = await publishFinding({
      findingId: requestFindingId,
      isPublished: true,
    });

    if (didPublish) {
      handleFindingPublicationChange(requestFindingId, true);
    }
  }, [findingId, handleFindingPublicationChange, publishFinding]);
  const handleSendFieldContextToChat = useCallback(
    ({
      field,
      fieldLabel,
      excerpt,
      lineStart,
      lineEnd,
    }: {
      field: string;
      fieldLabel: string;
      excerpt: string;
      lineStart?: number;
      lineEnd?: number;
    }) => {
      if (!finding) {
        return;
      }

      const mention = createFindingObjectContextMention(finding, {
        selection: {
          field,
          lineStart,
          lineEnd,
          excerpt,
        },
        instruction: `Refine the "${fieldLabel}" field using the selected excerpt.`,
      });

      sendHttpMentionsToChat({
        mentions: [mention],
        contextMessage: `Refine the ${fieldLabel} field for this finding.`,
        linkedObject: {
          kind: 'Finding',
          id: finding.id,
          label: finding.title,
        },
      });
    },
    [finding, sendHttpMentionsToChat],
  );
  const handleOpenLinkedTicketDialog = useCallback(() => {
    setIsLinkedTicketDialogOpen(true);
  }, []);
  const handleCloseLinkedTicketDialog = useCallback(() => {
    if (isReplacingLinkedTicket) {
      return;
    }

    setIsLinkedTicketDialogOpen(false);
  }, [isReplacingLinkedTicket]);
  const handleReplaceLinkedTicket = useCallback(
    async ({
      provider,
      ticketUrl,
    }: {
      provider: LinkedTicketProvider;
      ticketUrl: string;
    }) => {
      if (!findingId) {
        return;
      }

      await replaceFindingLinkedTicket({
        findingId,
        provider,
        ticketUrl,
      });
    },
    [findingId, replaceFindingLinkedTicket],
  );

  const handleFindingAssigneeChange = useCallback(
    (assignedTo: string | undefined) => {
      const nextAssignedTo = assignedTo ?? '';
      let nextFields: EditableFields | undefined;
      let nextOriginal: EditableFields | null | undefined;

      setEditableFields((previousFields) => {
        nextFields = {
          ...previousFields,
          assignedTo: nextAssignedTo,
        };

        return nextFields;
      });
      setOriginalFields((previousOriginal) => {
        nextOriginal = previousOriginal
          ? {
              ...previousOriginal,
              assignedTo: nextAssignedTo,
            }
          : previousOriginal;

        return nextOriginal;
      });
      setSaveError(null);

      if (findingId && nextFields) {
        setFindingEditState(findingId, {
          editableFields: nextFields,
          mode,
          originalFields: nextOriginal,
        });
      }
    },
    [findingId, mode, setFindingEditState],
  );

  // Loading state
  if (isLoading) {
    return (
      <Box h="full" w="full" p="6">
        <VStack gap="4" alignItems="flex-start" w="full">
          <Skeleton h={8} w="200px" />
          <Skeleton h={6} w="300px" />
          <Skeleton h="400px" w="full" />
        </VStack>
      </Box>
    );
  }

  // Error or missing data state
  if (error || !findingId || !finding) {
    return (
      <Box
        h="full"
        w="full"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <VStack gap="4" textAlign="center">
          <EntityIcon type="finding" appearance="highlighted" density="large" />
          <HeadingItem color="fg.default">
            {tFindingDetail('notFound')}
          </HeadingItem>
          <BodyDefault color="fg.muted">
            {tFindingDetail('loadFailed')}
          </BodyDefault>
        </VStack>
      </Box>
    );
  }

  const linkedTicketReference = finding.externalReferences?.find(
    (ref) => ref.url,
  );
  const linkedTicketUrl = linkedTicketReference?.url ?? null;
  const linkedTicketProviderFromReference =
    linkedTicketReference &&
    isLinkedTicketProvider(linkedTicketReference.provider)
      ? linkedTicketReference.provider
      : null;
  const configuredLinkedTicketProvider =
    finding.externalReferences
      ?.map((externalReference) => externalReference.provider)
      .find((provider) => isLinkedTicketProvider(provider)) ?? null;
  const linkedTicketProvider =
    linkedTicketProviderFromReference ?? configuredLinkedTicketProvider;
  const canEditLinkedTicket = linkedTicketProvider !== null;
  const hasLinkedTicket = linkedTicketUrl !== null;
  const displayedSeverity = isEditSurface
    ? editableFields.severity
    : finding.severity;
  const displayedStatus = isEditSurface
    ? editableFields.status
    : finding.status;
  const displayedCategory = isEditSurface
    ? editableFields.category
    : finding.category;
  const isDisplayedPublished = isEditSurface
    ? editableFields.isPublished
    : finding.isPublished;
  const isRetestDisabled =
    isSaving ||
    isRetesting ||
    retestAbort.isShowingAbortedChip ||
    !finding.signalId ||
    !isDisplayedPublished ||
    isArchivedLifecycle(finding.lifecycle);
  const retestDisabledReason = isArchivedLifecycle(finding.lifecycle)
    ? tLifecycle('archivedFindingActionTooltip')
    : !isDisplayedPublished
      ? tFindingDetail('retestDisabledUnpublishedTooltip')
      : undefined;
  const pocSectionActions =
    mode === 'view' ? (
      <FindingPocSectionActions
        finding={finding}
        t={tFindingDetail}
        onGeneratePocScript={pocScriptActions.onGeneratePocScript}
        onRegeneratePocScript={pocScriptActions.onRegeneratePocScript}
        onDownloadPocScript={pocScriptActions.onDownloadPocScript}
        onDeletePocScript={pocScriptActions.onDeletePocScript}
        isGeneratingPocScript={pocScriptActions.isGeneratingPocScript}
        isDeletingPocScript={pocScriptActions.isDeletingPocScript}
        pocActionsMode={PocActionsMode.Dropdown}
        mutationsDisabled={isArchivedLifecycle(finding.lifecycle)}
        mutationsDisabledTooltip={tLifecycle('archivedFindingActionTooltip')}
      />
    ) : null;

  const renderFindingDetailsMetaRow = (trailingActions?: ReactNode) => (
    <HStack
      gap="2"
      alignItems="center"
      justifyContent="space-between"
      w="full"
      flexWrap="wrap"
    >
      <HStack gap="2" alignItems="center" flexWrap="wrap">
        {isEditSurface ? (
          <BadgeSeverity severity={displayedSeverity} size="sm">
            {displayedSeverity}
          </BadgeSeverity>
        ) : (
          <FindingSeverityBadge finding={finding} size="sm" />
        )}
        {isSeverityChangeRequestEnabled && finding.severityChangeRequest ? (
          <SeverityChangeRequestHeaderPill
            severityChangeRequest={finding.severityChangeRequest}
            dateFormatter={dateFormatter}
            onScrollToCard={handleScrollToSeverityChangeRequestCard}
          />
        ) : null}
        <FindingCvssScoresPill finding={finding} />
        <BadgeStatus
          status={
            isMappedFindingStatus(displayedStatus)
              ? FINDING_STATUS_BADGE_STATUS_MAP[displayedStatus]
              : BADGE_STATUS.UNRESOLVED
          }
          size="sm"
        >
          {isMappedFindingStatus(displayedStatus)
            ? formatFindingStatusForDisplay(displayedStatus)
            : formatFindingStatusForDisplay('Unresolved')}
        </BadgeStatus>
        {displayedCategory ? (
          <Badge variant="outline" size="sm" colorPalette="gray">
            {formatCategoryForDisplay(displayedCategory)}
          </Badge>
        ) : null}
        <SecurityClassificationBadges
          cweIds={
            finding.cweIds?.length ? finding.cweIds : finding.signal?.cweIds
          }
          owaspCategory={finding.owaspCategory ?? finding.signal?.owaspCategory}
          density="compact"
        />
      </HStack>
      <HStack gap="2" alignItems="center" flexWrap="wrap">
        <FindingPublicationBadge
          findingId={finding.id}
          isPublished={!!isDisplayedPublished}
          isArchived={isArchivedLifecycle(finding.lifecycle)}
          interactive={mode === 'view'}
          onPublicationChange={handleFindingPublicationChange}
        />
        <AssigneeSelector
          kind="finding"
          findingId={finding.id}
          selectedId={
            initializedEditableFindingIdRef.current === finding.id
              ? editableFields.assignedTo || undefined
              : finding.assignedTo || undefined
          }
          onAssigneeChange={handleFindingAssigneeChange}
        />
        {showRetestStatusBadge ? (
          <FindingRetestStatusBadge
            isRetesting={isRetesting}
            retestCompletedResult={retestCompletedResult}
            onViewActiveRetestTrace={
              activeRetestThreadId &&
              (finding.targetType === 'WEB_APPLICATION' ||
                finding.targetType === 'AI_APPLICATION' ||
                activeRetestExecutionId)
                ? handleViewActiveRetestTrace
                : undefined
            }
            abort={{
              isShowingAbortedChip: retestAbort.isShowingAbortedChip,
              isPending: retestAbort.isPending,
              onConfirm: retestAbort.abort,
              onRunAgain: handleRequestRetest,
            }}
          />
        ) : (
          <Badge variant="outline" size="sm">
            {`${findingOverviewT('wasRetestedLabel')}: ${wasRetestedBadgeLabel}`}
          </Badge>
        )}
        {trailingActions}
      </HStack>
    </HStack>
  );

  const viewModeRetestAction = (
    <Tooltip
      title={retestDisabledReason ?? ''}
      disabled={!retestDisabledReason}
    >
      <Box display="inline-block">
        <Button
          variant="ghost"
          size="xs"
          onClick={handleRequestRetest}
          loading={isRetesting}
          disabled={isRetestDisabled}
        >
          <TestTube01Icon size={14} />
          {tFindingDetail('retestButton')}
        </Button>
      </Box>
    </Tooltip>
  );

  return (
    <ChatHarProvider
      threadId={currentRunValue}
      fetchHarById={fetchHarById}
      fetchHarData={handleFetchHarData}
    >
      <CredentialEntityResolver credentials={credentials}>
        <Box h="full" w="full" overflow="hidden">
          {isEditSurface ? (
            <FindingDetailsEditView
              eyebrow={t('editingFindingDetails')}
              title={editableFields.title || finding.title}
              entityTitle={tFindingDetail('entityTitle')}
              findingId={finding.id}
              hasUnsavedChanges={hasUnsavedChanges}
              hasPendingSuggestions={hasPendingSuggestions}
              isSaving={isSaving}
              isPublished={!!isDisplayedPublished}
              isPublishing={isPublishing}
              isArchived={isArchivedLifecycle(finding.lifecycle)}
              publishLabel={tFindingsRow('publish')}
              saveChangesLabel={t('saveChanges')}
              resetLabel={t('reset')}
              cancelLabel={t('cancel')}
              unsavedChangesLabel={t('unsavedChanges')}
              allChangesSavedLabel={t('allChangesSaved')}
              meta={renderFindingDetailsMetaRow()}
              suggestionReviewBar={suggestionReviewBar}
              onReset={handleReset}
              onCancel={handleCancel}
              onSave={handleSave}
              onPublish={handlePublishFinding}
              onStopCopilotWorking={() => {
                void handleStopCopilotWorking();
              }}
              editFormProps={{
                finding,
                editableFields,
                saveError,
                hasUnsavedChanges,
                statusOptions,
                categoryOptions,
                signalsCollection,
                runsCollection,
                assetsCollection,
                isSearchingSignals,
                isSearchingRuns,
                isSearchingAssets,
                currentRunValue,
                cvssCalculatorVersion,
                findingCvssVersion,
                applicationCvssEnvironmentalRequirements:
                  applicationForCvss?.cvssEnvironmentalRequirements,
                linkedTicketUrl,
                canEditLinkedTicket,
                hasLinkedTicket,
                sendSelectionToChatLabel,
                handleFieldChange,
                handleStatusFieldChange,
                handlePocBlocksChange,
                handleCvssScoreChange,
                originalCvss: originalFields?.cvss,
                originalFields,
                onReasonsChange: handleCvssReasonsChange,
                handleOwaspRiskScoreChange,
                handleAssetsMultiChange,
                handleSignalSearch,
                handleRunSearch,
                handleAssetSearch,
                handleRunChange,
                handleSendFieldContextToChat,
                handleOpenLinkedTicketDialog,
                getMarkableFieldProps,
              }}
            />
          ) : (
            <VStack gap="0" h="full" alignItems="stretch">
              <Box
                px="4"
                py="3"
                borderBottomWidth="1px"
                borderColor="border.subtle"
                bg="bg.default"
              >
                <VStack gap="2" alignItems="flex-start" w="full">
                  <HStack gap="3" alignItems="center" w="full">
                    <EntityIcon
                      type="finding"
                      appearance="highlighted"
                      density="large"
                    />
                    <Tooltip title={finding.title}>
                      <Box flex="1" minW="0">
                        <HStack gap="2" alignItems="center" minW="0" w="full">
                          <HeadingItem
                            color="fg.default"
                            truncate
                            title={finding.title}
                            w="full"
                          >
                            {finding.title}
                          </HeadingItem>
                          <ArchivedLifecycleBadge
                            lifecycle={finding.lifecycle}
                          />
                        </HStack>
                      </Box>
                    </Tooltip>
                    <HStack gap="2">
                      <FindingDetailsHeaderActions
                        findingId={finding.id}
                        isPublished={!!finding.isPublished}
                        isExporting={isExporting}
                        isPublishing={isPublishing}
                        isArchived={isArchivedLifecycle(finding.lifecycle)}
                        findingIds={findingIds}
                        entityTitle={tFindingDetail('entityTitle')}
                        onSendToChat={handleSendToChat}
                        onEdit={handleEnterEditMode}
                        onExport={() => {
                          if (findingId) {
                            setExportDialogOpen(true);
                          }
                        }}
                        onPublish={handlePublishFinding}
                        onNavigate={navigateToFinding}
                      />
                      <CopyIdButton
                        id={finding.id}
                        entityName={tFindingDetail('entityTitle')}
                      />
                    </HStack>
                  </HStack>

                  {renderFindingDetailsMetaRow(viewModeRetestAction)}
                </VStack>
              </Box>

              <Box
                flex="1"
                minH={0}
                overflow="hidden"
                display="flex"
                flexDirection="column"
              >
                <Tabs.Root
                  value={activeDetailSubTab}
                  onValueChange={(details) => {
                    if (isFindingDetailsContentTab(details.value)) {
                      handleDetailSubTabChange(details.value);
                    }
                  }}
                  size="sm"
                  w="full"
                  flex="1"
                  minH={0}
                  display="flex"
                  flexDirection="column"
                  overflow="hidden"
                  lazyMount
                  unmountOnExit
                >
                  <FindingDetailsTabTriggers />

                  <Tabs.Content
                    value="overview"
                    flex="1"
                    overflow="hidden"
                    pt="0"
                  >
                    <Box h="full" overflow="hidden">
                      <Scroll orientation="vertical" showScrollbar h="full">
                        <Box p="4">
                          <VStack gap={4} alignItems="stretch" w="full">
                            {isSeverityChangeRequestEnabled &&
                            finding.severityChangeRequest ? (
                              <SeverityChangeRequestReviewCard
                                findingId={finding.id}
                                currentSeverity={finding.severity}
                                severityChangeRequest={
                                  finding.severityChangeRequest
                                }
                                canDecide={isTerraUser}
                                dateFormatter={dateFormatter}
                              />
                            ) : null}
                            <VStack gap={2} alignItems="stretch" w="full">
                              <BodyStrong>
                                {findingOverviewT('description')}
                              </BodyStrong>
                              <ObjectContextMarkableField
                                field="description"
                                fieldLabel={findingOverviewT('description')}
                                fullText={finding.description ?? ''}
                                sendToChatLabel={sendSelectionToChatLabel}
                                {...getMarkableFieldProps('description')}
                                onSendSelection={handleSendFieldContextToChat}
                              >
                                <Box color="fg.muted">
                                  <MarkdownPreview
                                    source={
                                      finding.description ||
                                      findingOverviewT('noDescription')
                                    }
                                    fontSize={14}
                                  />
                                </Box>
                              </ObjectContextMarkableField>
                            </VStack>
                            <FindingDrawerOverviewTab
                              finding={finding}
                              isLoading={isLoadingTimeline}
                              findingTimeline={findingTimeline}
                              onViewTrace={handleViewTrace}
                              onViewSignal={handleViewSignal}
                              onViewAsset={handleViewAsset}
                              onViewCodeChange={handleViewCodeChange}
                              pocSectionActions={pocSectionActions}
                              linkedFindings={linkedFindings}
                              onNavigateToFinding={navigateToFinding}
                              chainOrder={
                                constituentFindingIds.length > 0
                                  ? constituentFindingIds
                                  : undefined
                              }
                              isSeverityChangeRequestEnabled={
                                isSeverityChangeRequestEnabled
                              }
                              hideDescription
                              hidePoc
                            />
                            {finding.poc?.trim() ||
                            (finding.pocBlocks &&
                              finding.pocBlocks.length > 0) ? (
                              <VStack gap={2} alignItems="stretch" w="full">
                                <BodyStrong>
                                  {findingOverviewT('poc')}
                                </BodyStrong>
                                <ObjectContextMarkableField
                                  field="poc"
                                  fieldLabel={findingOverviewT('poc')}
                                  fullText={finding.poc ?? ''}
                                  sendToChatLabel={sendSelectionToChatLabel}
                                  {...getMarkableFieldProps('poc')}
                                  onSendSelection={handleSendFieldContextToChat}
                                >
                                  <Box w="full" color="fg.muted">
                                    {finding.pocBlocks &&
                                    finding.pocBlocks.length > 0 ? (
                                      <StructuredPoc
                                        blocks={finding.pocBlocks}
                                        findingId={finding.id}
                                      />
                                    ) : (
                                      <MarkdownPreview
                                        source={finding.poc ?? ''}
                                        fontSize={14}
                                      />
                                    )}
                                  </Box>
                                </ObjectContextMarkableField>
                              </VStack>
                            ) : null}
                          </VStack>
                        </Box>
                      </Scroll>
                    </Box>
                  </Tabs.Content>

                  <Tabs.Content
                    value="resolution"
                    flex="1"
                    overflow="hidden"
                    pt="0"
                  >
                    <Box h="full" overflow="hidden">
                      <Scroll orientation="vertical" showScrollbar>
                        <Box p="4">
                          {hasLinkedTicket && (
                            <VStack gap={2} mb={4} alignItems="stretch">
                              <BodyStrong>
                                {tFindingDetail('tabs.overview.linkedTicket')}
                              </BodyStrong>
                              <FindingLinkedTicketActions
                                linkedTicketUrl={linkedTicketUrl}
                                showEditLinkedTicketAction={false}
                                editLinkedTicketLabel={tFindingDetail(
                                  'tabs.overview.editLinkedTicket',
                                )}
                                onEditLinkedTicket={
                                  handleOpenLinkedTicketDialog
                                }
                              />
                            </VStack>
                          )}
                          <FindingDrawerResolutionTab finding={finding} />
                        </Box>
                      </Scroll>
                    </Box>
                  </Tabs.Content>

                  <Tabs.Content
                    value="business-impact"
                    flex="1"
                    overflow="hidden"
                    pt="0"
                  >
                    <Box h="full" overflow="hidden">
                      <Scroll orientation="vertical" showScrollbar>
                        <Box p="4">
                          {finding.businessImpact?.trim() ? (
                            <ObjectContextMarkableField
                              field="businessImpact"
                              fieldLabel={tFindingDetail(
                                'tabs.businessImpact.title',
                              )}
                              fullText={finding.businessImpact ?? ''}
                              sendToChatLabel={sendSelectionToChatLabel}
                              {...getMarkableFieldProps('businessImpact')}
                              onSendSelection={handleSendFieldContextToChat}
                            >
                              <Box color="fg.muted">
                                <MarkdownPreview
                                  source={finding.businessImpact ?? ''}
                                  fontSize={14}
                                />
                              </Box>
                            </ObjectContextMarkableField>
                          ) : (
                            <FindingDrawerBusinessImpactTab finding={finding} />
                          )}
                        </Box>
                      </Scroll>
                    </Box>
                  </Tabs.Content>

                  <Tabs.Content value="audit" flex="1" overflow="hidden" pt="0">
                    <Box h="full" overflow="hidden">
                      <Scroll orientation="vertical" showScrollbar>
                        <Box p="4">
                          <FindingDrawerAuditTab
                            auditLogs={auditLogs}
                            isLoading={isLoadingAuditLogs}
                            isSeverityChangeRequestEnabled={
                              isSeverityChangeRequestEnabled
                            }
                            dateFormatter={dateFormatter}
                          />
                        </Box>
                      </Scroll>
                    </Box>
                  </Tabs.Content>
                  <FindingCvssTabPanel finding={finding} />
                </Tabs.Root>
              </Box>
            </VStack>
          )}

          <FindingsExportDialog
            isOpen={isExportDialogOpen}
            onOpenChange={(open) => {
              setExportDialogOpen(open);
              if (!open) {
                resetExport();
              }
            }}
            onStartExport={async ({ includeRemediation }) => {
              if (!findingId) {
                return;
              }

              await exportFindingsPdf([findingId], {
                includeRemediation,
              });
            }}
            isExporting={isExporting}
            exportStatus={exportStatus}
            progress={exportProgress}
            findingsCount={1}
            error={exportError ?? undefined}
          />

          <ConfirmationDialog
            isOpen={showCloseConfirmation}
            onOpenChange={({ open }: { open: boolean }) =>
              setShowCloseConfirmation(open)
            }
            title={t('confirmClose.title')}
            description={t('confirmClose.description')}
            confirmText={t('confirmClose.confirm')}
            cancelText={t('confirmClose.cancel')}
            onConfirm={handleConfirmClose}
            variant="warning"
          />
          <ConfirmationDialog
            isOpen={isRetestStatusConfirmationOpen}
            onOpenChange={({ open }: { open: boolean }) =>
              setIsRetestStatusConfirmationOpen(open)
            }
            title={t('retestStatusConfirmation.title')}
            description={t('retestStatusConfirmation.description')}
            confirmText={tFindingDetail('retestButton')}
            cancelText={t('confirmClose.cancel')}
            onConfirm={handleConfirmRetestStatusSave}
            loading={isSaving}
            variant="warning"
          />
          <ConfirmationDialog
            isOpen={isScheduledRetestConfirmationOpen}
            onOpenChange={({ open }: { open: boolean }) =>
              setIsScheduledRetestConfirmationOpen(open)
            }
            title={tFindingDetail('retest.scheduledRetestConfirmation.title')}
            description={tFindingDetail(
              'retest.scheduledRetestConfirmation.description',
              {
                scheduledAt: finding?.scheduledRetestAt
                  ? new Date(finding.scheduledRetestAt).toLocaleString()
                  : '',
              },
            )}
            confirmText={tFindingDetail(
              'retest.scheduledRetestConfirmation.confirm',
            )}
            cancelText={tFindingDetail(
              'retest.scheduledRetestConfirmation.cancel',
            )}
            onConfirm={handleConfirmScheduledRetest}
            loading={isRetesting}
            showIcon={false}
          />
          {statusReasonDialog}
          {signalFalsePositiveDialog}
          <UnpublishedConstituentsDialog
            isOpen={unpublishedConstituentsViolations !== null}
            onOpenChange={(open) => {
              if (!open) setUnpublishedConstituentsViolations(null);
            }}
            violations={unpublishedConstituentsViolations ?? []}
            onNavigateToFinding={(linkedFindingId) => {
              setUnpublishedConstituentsViolations(null);
              navigateToFinding(linkedFindingId);
            }}
          />
          <EditLinkedTicketDialog
            isOpen={isLinkedTicketDialogOpen}
            provider={linkedTicketProvider}
            ticketUrl={linkedTicketUrl ?? ''}
            isReplacingLinkedTicket={isReplacingLinkedTicket}
            onClose={handleCloseLinkedTicketDialog}
            onReplaceLinkedTicket={handleReplaceLinkedTicket}
          />
        </Box>
      </CredentialEntityResolver>
    </ChatHarProvider>
  );
}
